import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { SpinningButton } from "Components/ReactstrapSlim";
import { InputFL, SelectInputFL, ValidationSummary } from "Components/Inputs";
import { AccountClient, MFAMethod, TwoFactorResponse } from "Services/LegacyAPI/AccountClient";
import { useApiClient } from "Services/API/useApiClient";
import exhaustiveCheck from "Utils/exhaustiveCheck";
import { AccountMFAClient, OTPTransportMethod, SendCodeResultModel } from "Services/API/API";
import moment from "moment";
import { useRetryAfter } from "Hooks/useRetryAfter";

interface TwoFactorConfirmFormModel {
   MFAToken: string;
   MFAMethod: MFAMethod;
}

const TwoFactorConfirmFormSchema: yup.SchemaOf<TwoFactorConfirmFormModel> = yup.object({
   MFAToken: yup.string().required("Confrimation code is required").defined(),
   MFAMethod: yup.mixed().oneOf<MFAMethod>([MFAMethod.Authy, MFAMethod.EMAIL, MFAMethod.SMS, MFAMethod.TOTP]).required("Method is required").defined()
}).defined();


export interface MFAData {
   phoneNumberLast4: string;
   mfaMethods: MFAMethod[];
   email: string;
   password: string;
}

export interface TwoFactorConfirmFormProps {
   isActive: boolean;
   onSuccess: (logonResult: TwoFactorResponse) => boolean;

   mfaData?: MFAData;
}

export const TwoFactorConfirmForm: React.FC<TwoFactorConfirmFormProps> = function ({ isActive, onSuccess, mfaData }) {
   const accountClient = useApiClient(AccountClient);
   const [isLoading, setIsLoading] = useState<boolean>(false);
   const [serverErrors, setServerErrors] = useState<Array<[string, string]>>([]);

   const formMethods = useForm<TwoFactorConfirmFormModel>({
      resolver: yupResolver(TwoFactorConfirmFormSchema),
      mode: "onSubmit"
   });

   useEffect(() => {
      formMethods.reset({
         MFAMethod: mfaData && (mfaData.mfaMethods.length ?? 0 > 0) ? mfaData.mfaMethods[0] : undefined,
         MFAToken: ""
      });
   }, [mfaData?.mfaMethods]);

   const onSubmit = async () => {
      if (isLoading)
         return;
      setIsLoading(true);
      setServerErrors([]);
      try {
         const isValid = await formMethods.trigger();
         if (isValid) {
            const formValues = formMethods.getValues();
            const twoFactorRegisterResult = await accountClient.logon({
               ...formValues,
               UserName: mfaData?.email,
               Password: mfaData?.password
            });
            if (twoFactorRegisterResult.success) {
               onSuccess(twoFactorRegisterResult);
            } else {
               setServerErrors([["email", twoFactorRegisterResult.error]]);
            }
         }
      } catch (err: any) {
         if (err.error) {
            setServerErrors([["email", err.error]]);
         }
         console.log(err);
      } finally {
         setIsLoading(false);
      }
   }

   useEffect(() => {
      if (isActive) {
         setTimeout(function () {
            formMethods.setFocus("MFAToken");
         }, 500);
      }
   }, [isActive]);

   const method = formMethods.watch("MFAMethod");
   const mfaClient = useApiClient(AccountMFAClient);

   const { retryAfterDate, setRetryAfterDate, retryAfter } = useRetryAfter();

   const onSend = async (transport: OTPTransportMethod) => {
      setIsLoading(true);
      try {
         const result = await mfaClient.sendCode(mfaData?.email, transport);
         setRetryAfterDate(moment(result.retryAfter).toDate());
      }
      catch (err) {
         if (err instanceof SendCodeResultModel) {
            setRetryAfterDate(moment(err.retryAfter).toDate());
         }
      }
      finally {
         setIsLoading(false);
      }
   }

   return (
      <div className="signing-mode-inner">
         <div className="signing-header">
            Two factor authentication
         </div>

         <div className="signing-two-factor-form">
            <form onSubmit={formMethods.handleSubmit(onSubmit)} className="signing-two-factor-form">
               {
                  mfaData && mfaData.mfaMethods.length > 1
                     ? <SelectInputFL disabled={!!retryAfterDate || isLoading} control={formMethods.control} name="MFAMethod" placeholder="Select a method" className="stff-input" >
                        {
                           mfaData.mfaMethods.map(m => (<option key={m} value={m}>{mfaMethodLabel(m)}</option>))
                        }
                     </SelectInputFL>
                     : <input type="hidden" {...formMethods.register("MFAMethod")} />
               }
               {
                  method === MFAMethod.SMS
                     ? <>
                        <div className="signing-caption">
                           A confirmation code will be sent to your phone number ending in {mfaData?.phoneNumberLast4}.
                        </div>
                        <SpinningButton disabled={!!retryAfterDate || isLoading} isLoading={isLoading} fluid color="primary" size="md" className="stff-cta" type="button" onClick={() => onSend(OTPTransportMethod.SMS)}>
                           {
                              retryAfterDate && retryAfter > 0
                                 ? <span>Retry after {retryAfter}s</span>
                                 : <span>Send code</span>
                           }
                        </SpinningButton>
                     </>
                     : method === MFAMethod.EMAIL
                        ? <> <div className="signing-caption">
                           A confirmation code will be sent to your email address.
                        </div>
                           <SpinningButton disabled={!!retryAfterDate || isLoading} isLoading={isLoading} fluid color="primary" size="md" className="stff-cta" type="button"
                              onClick={() => onSend(OTPTransportMethod.EMAIL)}>
                              {
                                 retryAfterDate && retryAfter > 0
                                    ? <span>Retry after {retryAfter}s</span>
                                    : <span>Send code</span>
                              }
                           </SpinningButton>
                        </>
                        : <div className="signing-caption">
                           Use your authenticator app to get the confirmation code and enter it below.
                        </div>
               }
               <ValidationSummary errors={formMethods.formState.errors} serverErrors={serverErrors} className="text-center" />
               <InputFL disabled={isLoading} control={formMethods.control} name="MFAToken" placeholder="Confirmation code" className="stff-input" autoComplete="off" />
               <SpinningButton type="submit" isLoading={isLoading} onClick={onSubmit} color="primary" size="md" fluid className="stff-cta">Confirm</SpinningButton>
            </form>
         </div>
      </div>
   )
}

function mfaMethodLabel(method: MFAMethod): string {
   switch (method) {
      case MFAMethod.Authy:
         return "Authenticator app";
      case MFAMethod.EMAIL:
         return "Email";
      case MFAMethod.SMS:
         return "SMS";
      case MFAMethod.TOTP:
         return "Time-based one-time password";
      case MFAMethod.FIDO2:
         return "Passkey";
      default:
         exhaustiveCheck(method);
         return method;
   }
}